import React from 'react';

import {TFunction} from 'i18next';
import {generatePath, useParams} from 'react-router-dom';

import {PermissionAccess} from 'components/common/PermissionAccess';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';

import {AggregateContentTable} from 'components/pages/ProductsDetail/AggregateContent/Table';
import {Link} from 'components/ui-kit/Link';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {PRODUCT_STATUS_COLORS} from 'core/constants/statuses';
import {ProductsPackageType, ProductStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {
    GridFieldConfig,
    ProductContentAccessors,
    ProductContentFormattedRow,
    ProductContentRawRow,
    ProductsGridAccessors,
    UserPermission,
} from 'types';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {toHex} from 'utils/string';

import {BreadcrumbsHeader} from '../Breadcrumbs';
import {Wrapper} from '../styled';

const getGridColumns = (t: TFunction): GridFieldConfig[] => [
    {
        id: ProductContentAccessors.productGroupCode,
        header: t('pages.products.grid.productGroup'),
        isSortable: false,
        width: 150,
    },
    {
        id: ProductContentAccessors.name,
        header: t('pages.products.grid.product'),
        isSortable: false,
        width: 200,
    },
    {
        id: ProductContentAccessors.packageType,
        header: t('pages.products.grid.package'),
        isSortable: false,
        width: 120,
    },
    {
        id: ProductContentAccessors.code,
        header: t('pages.products.grid.code'),
        isSortable: false,
        width: 260,
    },
    {
        id: ProductContentAccessors.modifiedDate,
        header: t('pages.products.grid.changeDate'),
        isSortable: false,
        width: 120,
    },
    {
        id: ProductContentAccessors.parentMarkingCode,
        header: t('pages.products.grid.parentCode'),
        isSortable: false,
        width: 220,
    },
    {
        id: ProductContentAccessors.status,
        header: t('pages.products.grid.status'),
        isSortable: false,
        width: 180,
    },
    {
        id: ProductContentAccessors.authenticStatus,
        header: t('pages.products.grid.authenticStatus'),
        isSortable: false,
        width: 130,
    },
    {
        id: ProductContentAccessors.storageLocationAddress,
        header: t('pages.products.grid.storageLocationAddress'),
        isSortable: false,
        width: 200,
    },
    {
        id: ProductContentAccessors.owner,
        header: t('pages.products.grid.owner'),
        isSortable: false,
        width: 200,
    },
];

export function convertData(
    data: ProductContentRawRow[] | undefined,
    t: TFunction,
): ProductContentFormattedRow[] {
    if (!data) return [];

    return data.map((elem) => ({
        [ProductContentAccessors.productGroupCode]: handleNil(elem.productGroupCode, (value) => (
            <ProductGroupIcon value={value} label />
        )),
        [ProductContentAccessors.name]: handleNil(elem.productName),
        [ProductContentAccessors.code]: handleNil(elem.markingCode, (value: string) => (
            <Link
                to={generatePath(ROUTE.PRODUCTS_DETAIL, {
                    id: toHex(`${elem.gtin}§${value}`),
                })}
                style={{wordBreak: 'break-all'}}
            >
                {value}
            </Link>
        )),
        [ProductContentAccessors.modifiedDate]: handleNil(elem.modifiedAt, (value: string) =>
            formatDate(value, 'dd.MM.yyyy'),
        ),
        [ProductsGridAccessors.parentMarkingCode]: handleNil(
            elem.parentMarkingCode,
            (value: string) => (
                <PermissionAccess
                    permissions={[
                        UserPermission.PRODUCT_DETAIL_VIEW,
                        UserPermission.PRODUCT_CARD_VIEW,
                    ]}
                    placeholder={value}
                >
                    {value && elem.parentGtin ? (
                        <Link
                            to={generatePath(ROUTE.PRODUCTS_DETAIL, {
                                id: toHex(`${elem.parentGtin}§${value}`),
                            })}
                            style={{wordBreak: 'break-all'}}
                        >
                            {value}
                        </Link>
                    ) : (
                        <div>—</div>
                    )}
                </PermissionAccess>
            ),
        ),
        [ProductContentAccessors.packageType]: handleNil(
            elem.packagingType,
            (value: ProductsPackageType) => t(`dictionaries.products.packageType.${value}`),
        ),
        [ProductContentAccessors.manufacturer]: handleNil(elem.manufacturerName),
        [ProductContentAccessors.status]: handleNil(elem.status, (value: ProductStatus) => (
            <StatusWrapper color={PRODUCT_STATUS_COLORS[value ?? 'DEFAULT']}>
                {t(`dictionaries.products.status.${value}`)}
            </StatusWrapper>
        )),
        [ProductsGridAccessors.authenticStatus]: elem.authenticStatus
            ? t(`dictionaries.authenticStatus.${elem.authenticStatus}`)
            : '—',
        [ProductsGridAccessors.storageLocationAddress]: handleNil(elem.storageLocationAddress),
        [ProductsGridAccessors.owner]: handleNil(elem.owner),
    }));
}

export const DeclarationsAggregatedCodesPage = () => {
    const params = useParams<{markingCode: string; transportCode: string}>();

    return (
        <>
            <BreadcrumbsHeader />
            <Wrapper>
                <AggregateContentTable
                    markingCode={params.markingCode}
                    gtin="EMPTY"
                    configFunc={getGridColumns}
                    dataMapper={convertData}
                />
            </Wrapper>
        </>
    );
};
